import { SnackbarService } from '@adaptiva/core/snackbars';
import { UiService } from '@adaptiva/core/ui';
import { CpeInfo, CveMappingProperties, ECpeInfoPart, ICpeInfo, MetadataSerialization } from '@adaptiva/metadata/common';
import { EFluidInputType } from '@adaptiva/widgets/fluid-input';
import { ETableDisplay, IBuilderConfig, ObjectListBuilderComponent } from '@adaptiva/widgets/object-list-builder';
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { BaseAuthoringPropertiesComponent } from '../base-authoring-component';

@Component({
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'ada-cve-mapping-properties',
	templateUrl: './cve-mapping-properties.component.html'
})
export class CveMappingPropertiesComponent extends BaseAuthoringPropertiesComponent<CveMappingProperties> {

	@ViewChild('CpeMatchStringsInput')
	private _cpeMatchStringBuilder: ObjectListBuilderComponent<ICpeInfo>;

	public cpesBuilderConfig: IBuilderConfig[] = [
		{
			inputKey: 'part',
			inputType: EFluidInputType.INPUT_TYPE_COMBO,
			label: 'Part',
			placeholder: 'Part',
			required: true,
			options: ECpeInfoPart.displayValues(),
			rowDisplayTransformer: ECpeInfoPart.displayString,
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT,
		},
		{
			inputKey: 'vendor',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Vendor',
			placeholder: 'Vendor',
			required: true,
			tableDisplay: ETableDisplay.HEADER
		},
		{
			inputKey: 'product',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Product',
			placeholder: 'Product',
			required: true,
			tableDisplay: ETableDisplay.HEADER
		},
		{
			inputKey: 'version',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Version',
			placeholder: 'Version',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'update',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Update',
			placeholder: 'Update',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'edition',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Edition',
			placeholder: 'Edition',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'language',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Language',
			placeholder: 'Language',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'swEdition',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Software Edition',
			placeholder: 'Software Edition',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'targetSw',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Target Software',
			placeholder: 'Target Software',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'targetHw',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Target Hardware',
			placeholder: 'Target Hardware',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		},
		{
			inputKey: 'other',
			inputType: EFluidInputType.INPUT_TYPE_TEXT,
			label: 'Other',
			placeholder: 'Other',
			tableDisplay: ETableDisplay.EXPANDABLE_CONTENT
		}
	];

	public cpeStringToImport: string;

	public override propertyConstructor: MetadataSerialization.Deserializable<any> = CveMappingProperties;

	constructor(protected snackbarService: SnackbarService, _ui: UiService) {
		super(_ui);
	}

	public override commitModelToView(): void {
		this._cpeMatchStringBuilder.loadCustomObjects(this.cpeMatchStringInfo());
	}

	public cpeMatchStringInfo(): CpeInfo[] {
		return this.currentPropertyObject.CpeMatchStrings?.map((_searchString: string) =>
			CpeInfo.fromString(_searchString, false)
		) || [];
	}

	public deleteCpeMatchStrings(): void {
		this.deleteObjectPropertyValue('CpeMatchStrings');
		this._cpeMatchStringBuilder.loadCustomObjects([]);
	}

	public importCpeString(): void {
		const _cpeInfo: CpeInfo = CpeInfo.fromString(this.cpeStringToImport, false);
		if (_cpeInfo.validate()) 
			return this.snackbarService.display('The imported string does does not match requirements. Please make sure the CPE string is correct');

		const _matchInfo: CpeInfo[] = this.cpeMatchStringInfo();
		_matchInfo.push(_cpeInfo);

		this.setCpeMatchStrings(_matchInfo);
		this._cpeMatchStringBuilder.loadCustomObjects(_matchInfo);
		this.cpeStringToImport = '';
	}

	public setCpeMatchStrings(_searchInfo: ICpeInfo[]): void {
		this.setObjectPropertyValue('CpeMatchStrings', _searchInfo.map((_info: ICpeInfo) => CpeInfo.toString(_info)));
	}
}
